import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    Paper,
    Divider,
} from '@mui/material';
import {
    Laptop,
    Smartphone,
    Wifi,
    Cpu,
    Monitor,
    HardDrive,
    Tablet,
    Watch,
    Plus,
} from 'lucide-react';
import DevicesIcon from '@mui/icons-material/DevicesOutlined';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const assetTypeIcons = {
    'Laptop': <Laptop size={36} />,
    'Mobile Phone': <Smartphone size={36} />,
    'Data Card': <Wifi size={36} />,
    'Sim Card': <Cpu size={36} />,
    'Monitor': <Monitor size={36} />,
    'Storage': <HardDrive size={36} />,
    'Tablet': <Tablet size={36} />,
    'Smart Watch': <Watch size={36} />,
    'Other': <Plus size={36} />,
};

const statusColors = {
    'Issued': { bg: '#e3f2fd', text: '#1565c0', label: 'primary' },
    'Returned': { bg: '#e8f5e9', text: '#2e7d32', label: 'success' },
    'Damaged': { bg: '#ffebee', text: '#c62828', label: 'error' },
};

const MyAssets = () => {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMyAssets = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                if (!token) {
                    setError('You must be logged in to view your assets.');
                    return;
                }
                // Use 'me' alias â€” backend resolves this to the authenticated user's employee_id
                const response = await axios.get(`${API_URL}/api/assets/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true,
                });
                if (response.data.success) {
                    setAssets(response.data.data);
                } else {
                    setError('Failed to load your assets.');
                }
            } catch (err) {
                //console.error('Error fetching my assets:', err);
                if (err.response?.status === 400) {
                    setError('Could not resolve your Employee ID. Please contact HR.');
                } else {
                    setError('Failed to load your assets. Please try again later.');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchMyAssets();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4,  minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                
                <Typography variant="h4" sx={{ fontWeight: 'bold'}}>
                Asset Tracker
                </Typography>
            </Box>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4, ml: 7 }}>
                View the company assets currently assigned to you.
            </Typography>

            <Divider sx={{ mb: 4 }} />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {!error && assets.length === 0 && (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3, bgcolor: '#f8f9fa', border: '2px dashed #e0e0e0' }}>
                    <DevicesIcon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        No Assets Assigned
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        You have no company assets assigned to you at the moment. Contact HR if you believe this is incorrect.
                    </Typography>
                </Paper>
            )}

            {/* Summary Cards */}
            {assets.length > 0 && (
                <>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={4}>
                            <Card sx={{ borderRadius: 3, boxShadow: 2, borderLeft: '5px solid #1a237e' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography color="textSecondary" gutterBottom>Total Assets</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                                        {assets.length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card sx={{ borderRadius: 3, boxShadow: 2, borderLeft: '5px solid #1565c0' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography color="textSecondary" gutterBottom>Currently Issued</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                                        {assets.filter(a => a.status === 'Issued').length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card sx={{ borderRadius: 3, boxShadow: 2, borderLeft: '5px solid #c62828' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography color="textSecondary" gutterBottom>Damaged</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#c62828' }}>
                                        {assets.filter(a => a.status === 'Damaged').length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Asset Cards */}
                    <Grid container spacing={3}>
                        {assets.map((asset) => {
                            const statusStyle = statusColors[asset.status] || statusColors['Issued'];
                            const issuedDateStr = asset.issuedDate
                                ? new Date(asset.issuedDate).toLocaleDateString(undefined, {
                                    year: 'numeric', month: 'short', day: 'numeric'
                                })
                                : 'N/A';

                            return (
                                <Grid item xs={12} sm={6} md={4} key={asset.id}>
                                    <Card sx={{
                                        borderRadius: 3,
                                        boxShadow: 2,
                                        border: '1px solid #e0e0e0',
                                        transition: '0.3s',
                                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                                        height: '100%',
                                    }}>
                                        <CardContent sx={{ p: 3 }}>
                                            {/* Asset Type Icon + Name */}
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                                <Box sx={{
                                                    bgcolor: '#e8eaf6',
                                                    color: '#1a237e',
                                                    borderRadius: 2,
                                                    p: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    {assetTypeIcons[asset.assetType] || assetTypeIcons['Other']}
                                                </Box>
                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                                                        {asset.assetName}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {asset.assetType}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={asset.status}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: statusStyle.bg,
                                                        color: statusStyle.text,
                                                        fontWeight: 600,
                                                        borderRadius: 1,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            </Box>

                                            <Divider sx={{ mb: 2 }} />

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {asset.model && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="caption" color="textSecondary">Model</Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>{asset.model}</Typography>
                                                    </Box>
                                                )}
                                                {asset.serialNumber && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="caption" color="textSecondary">Serial No.</Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>{asset.serialNumber}</Typography>
                                                    </Box>
                                                )}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="caption" color="textSecondary">Issued On</Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>{issuedDateStr}</Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </>
            )}
        </Box>
    );
};

export default MyAssets;
